import React from 'react';
import { bool, node, string } from 'prop-types';
import classNames from 'classnames';

import LayoutComposer from '../LayoutComposer';
import LayoutWrapperAccountSettingsSideNav from './LayoutWrapperAccountSettingsSideNav';

import css from './LayoutSideNavigation.module.css';

// Commonly used layout
const LayoutSideNavigation = props => {
  const {
    className,
    rootClassName,
    containerClassName,
    mainColumnClassName,
    sideNavClassName,
    children,
    topbar: topbarContent,
    footer: footerContent,
    sideNav: sideNavContent,
    useAccountSettingsNav,
    currentPage,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const containerClasses = containerClassName || css.container;

  // TODO: since responsiveAreas are still experimental,
  //       we don't separate "aside" through layoutComposer
  const layoutAreas = `
    topbar
    main
    footer
  `;

  /* Account-settings pages flip the traditional left-aside layout to
   * a horizontal sub-nav-below-topbar pattern. Keeps the whole
   * workspace using the same horizontal nav vocabulary (UserNav on
   * top, AccountSettings sub-nav below) and frees the main column
   * from the narrow right-half real estate that used to host a
   * single form. The `sideNav` prop path still works for callers
   * that genuinely need an aside. */
  const isAccountSettingsLayout = useAccountSettingsNav;

  return (
    <LayoutComposer areas={layoutAreas} className={classes} {...rest}>
      {layoutProps => {
        const { Topbar, Main, Footer } = layoutProps;
        return (
          <>
            <Topbar as="header" className={css.topbar}>
              {topbarContent}
              {isAccountSettingsLayout ? (
                <LayoutWrapperAccountSettingsSideNav currentPage={currentPage} />
              ) : null}
            </Topbar>
            <Main
              as="div"
              className={classNames(containerClasses, {
                [css.containerStacked]: isAccountSettingsLayout,
              })}
            >
              {!isAccountSettingsLayout && sideNavContent ? (
                <aside className={classNames(css.sideNav, sideNavClassName)}>
                  {sideNavContent}
                </aside>
              ) : null}
              <main
                className={classNames(css.main, mainColumnClassName, {
                  [css.mainFull]: isAccountSettingsLayout,
                })}
              >
                {children}
              </main>
            </Main>
            <Footer>{footerContent}</Footer>
          </>
        );
      }}
    </LayoutComposer>
  );
};

LayoutSideNavigation.displayName = 'LayoutSideNavigation';

LayoutSideNavigation.defaultProps = {
  className: null,
  rootClassName: null,
  sideNav: null,
  footer: null,
  useAccountSettingsNav: false,
  currentPage: null,
};

LayoutSideNavigation.propTypes = {
  className: string,
  rootClassName: string,
  children: node.isRequired,
  topbar: node.isRequired,
  sideNav: node,
  footer: node,
  useAccountSettingsNav: bool,
  currentPage: string,
};

export default LayoutSideNavigation;
